/**
 * Drawer — el panel lateral del design system.
 *
 * Portado de `clobi-design-system@0.1.0`. El repo tiene tres cajones laterales
 * escritos a mano (`FilterDrawer` entre ellos) y ninguno atrapa el foco ni
 * declara `role="dialog"`.
 *
 * ⚠️ `modal={false}` deja la superficie de atrás usable y sin velo. Es el caso
 * de un panel de filtros que se mira mientras se lee la tabla — y por eso
 * tampoco atrapa el foco ni bloquea el scroll: atrapar el foco en algo que no
 * tapa nada deja al usuario encerrado sin entender por qué.
 */
import { useEffect, useId, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { GlassPanel } from './GlassPanel'
import { useDismissable } from './useDismissable'

export type DrawerSide = 'right' | 'left' | 'bottom'

export interface DrawerProps {
  open?: boolean
  onClose?: () => void
  /** Por defecto `'right'`. En ≤640px, `'right'` y `'left'` colapsan a `'bottom'`. */
  side?: DrawerSide
  /** Ancho —o alto si `side='bottom'`—. Número en px o cualquier valor CSS. */
  size?: string | number
  title?: ReactNode
  actions?: ReactNode
  /** `false` = sin velo, la superficie de atrás sigue usable. */
  modal?: boolean
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

export function Drawer({
  open = false,
  onClose,
  side = 'right',
  size = 420,
  title,
  actions,
  modal = true,
  children,
  className = '',
  style,
}: DrawerProps) {
  const idTitulo = useId()
  const contenedor = useDismissable({ open, onClose, modal })

  // El panel entra desplazado y se acomoda en el frame siguiente. Sin ese
  // frame de por medio el navegador pinta el estado final de una y no hay
  // transición: el panel aparece de golpe.
  const [entrando, setEntrando] = useState(true)
  useEffect(() => {
    if (!open) {
      setEntrando(true)
      return undefined
    }
    const raf = requestAnimationFrame(() => setEntrando(false))
    return () => cancelAnimationFrame(raf)
  }, [open])

  if (!open) return null

  const medida: CSSProperties =
    side === 'bottom'
      ? { height: size, maxHeight: '90dvh' }   // dvh y no vh: acompaña el colapso
      : { width: size, maxWidth: '100vw' }     // de la barra de direcciones

  const panel = (
    <GlassPanel
      as="div"
      level="solid"
      padded={false}
      ref={contenedor}
      role="dialog"
      aria-modal={modal ? 'true' : undefined}
      aria-labelledby={title ? idTitulo : undefined}
      className={`cl-drawer ${className}`.trim()}
      data-side={side}
      data-entering={entrando ? 'true' : 'false'}
      style={{ ...medida, ...style }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      {(title || actions) && (
        <div className="cl-drawer__header">
          {title && (
            <div
              id={idTitulo}
              style={{
                minWidth: 0,
                fontSize: 'var(--type-card-title-size)',
                fontWeight: 'var(--type-card-title-weight)' as CSSProperties['fontWeight'],
              }}
            >
              {title}
            </div>
          )}
          {actions && <div style={{ display: 'flex', gap: 6, flex: 'none' }}>{actions}</div>}
        </div>
      )}
      <div className="cl-drawer__body">{children}</div>
    </GlassPanel>
  )

  if (!modal) return panel

  return (
    <div className="cl-scrim" onClick={onClose}>
      {panel}
    </div>
  )
}
